import React, { useMemo } from 'react';
import { Paper } from '@material-ui/core';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import AllModuleAssessmentAcceleratorLayout from '../../../AccessmentAcceleratorComponents/AllModuleAssessmentAcceleratorLayout';
import StudentTable from './components/StudentTable';
import ActivityTable from './components/ActivityTable';
import ModuleTabSelector from '../../../AccessmentAcceleratorComponents/ModuleTabSelector';
import {
  buildAllModuleAssessmentData,
  buildAllStudentsClassroomData,
  buildClassAssessmentHeaderList,
  buildSingleClassroomModuleData,
  tableHasData
} from '../../../keyboarding-report-util';
import { KeyClassroomEngagementReportResult } from 'types/schema-generated-types';
import MaybeShowErrorMessageWithLoading from 'components/ReportsModule/components/common/MaybeShowErrorMessageWithLoading';
import { useTranslation } from 'react-i18next';
import useBooleanSwitch from '../../../hooks/use-boolean-switch';
import useModuleTabSelector from '../../../hooks/use-module-tab-selector';

type Props = {
  loading?: boolean;
  classroomReport: KeyClassroomEngagementReportResult;
};

const ClassroomAssessmentAcceleratorTableCard: React.FC<Props> = ({ classroomReport, loading }) => {
  const [{ selectedModule, showAll }, { setSelectedModule }] = useModuleTabSelector();
  const { t } = useTranslation('ReportsHelpCards');
  const getData = useBooleanSwitch(
    buildAllModuleAssessmentData(classroomReport?.assessmentAccelerator!),
    buildSingleClassroomModuleData(classroomReport, selectedModule)
  );
  const { data, students, ...rest } = getData(showAll);

  const getStudentData = useBooleanSwitch(
    buildAllStudentsClassroomData(classroomReport.students),
    students
  );
  const hasData = useMemo(() => tableHasData(data), [data]);
  const getHeaderList = useBooleanSwitch(classroomReport?.assessmentAccelerator, rest);

  return (
    <Paper>
      <ReportCard
        helpText={t('classAssessmentHelp', { ns: 'ReportsHelpCards' })}
        title="Assessment Accelerator"
        loading={loading!}
        endAdornment={
          <ModuleTabSelector onChange={setSelectedModule} selectedModule={selectedModule} />
        }
        reportData={classroomReport?.assessmentAccelerator?.activityTypes!}
      >
        <MaybeShowErrorMessageWithLoading loading={loading!} shouldRender={hasData}>
          <AllModuleAssessmentAcceleratorLayout
            showDivider={showAll}
            showModuleHeader={showAll}
            TopComponent={<ActivityTable selectedModule={selectedModule} data={data} />}
            BottomComponent={
              <StudentTable selectedModule={selectedModule} data={getStudentData(showAll)} />
            }
            headerList={buildClassAssessmentHeaderList(getHeaderList(showAll), t)}
            title={selectedModule}
          />
        </MaybeShowErrorMessageWithLoading>
      </ReportCard>
    </Paper>
  );
};

export default ClassroomAssessmentAcceleratorTableCard;
